import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  Injector,
  Renderer2,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  FieldWrapper,
  FormlyFieldConfig,
  FormlyFieldProps,
} from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { InputText } from 'primeng/inputtext';
import { CustomDrawerComponent } from '../../_shared/_components/drawer/drawer.component';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';

export interface DrawerWrapperProps extends FormlyFieldProps {
  position?: typeof Drawer.prototype.position;
  buttonLabel?: string;
  setter?: any;
  status?: boolean | undefined;
  formatLabel: any;
  isTextView: boolean | undefined;
  onShow?: WritableSignal<any>;
  labelColor?: string;
}

@Component({
  selector: 'drawer-wrapper-primeng-form-field',
  standalone: true,
  imports: [
    CommonModule,
    ...FORM_IMPORTS,
    InputText,
    Button,
    TranslatePipe,
    CustomDrawerComponent,
  ],
  template: `
    @if (props.isTextView) {
      <div class="w-full flex flex-col gap-2 p-field">
        <label class="w-full {{ props.labelColor ?? 'text-black' }}">
          {{ props.label }}
        </label>
        <div class="w-full" [innerHTML]="props.placeholder"></div>
        <p-button
          [label]="props.buttonLabel ?? 'Set'"
          size="small"
          class="self-end"
          (onClick)="visible = true"
        ></p-button>
      </div>
    } @else {
      <div class="p-field">
        <label
          class="ml-[var(--p-inputtext-padding-x)]"
          [for]="id"
          [innerHTML]="props.label"
        >
        </label>
        <input
          [formControl]="control"
          #inputDrawer
          pInputText
          readonly
          [placeholder]="props.placeholder"
          [type]="'text'"
          class="w-full {{ showError ? 'ng-dirty ng-invalid' : '' }} {{
            props.readonly ? 'bg-[var(--p-select-disabled-background)]' : ''
          }}"
          [ngModel]="value()"
          [disabled]="props.readonly ?? false"
          (click)="props.readonly ? null : (visible = true)"
        />
      </div>
    }
    @if (props.description) {
      <small
        class="italic ml-[var(--p-inputtext-padding-x)]"
        [innerHTML]="props.description"
      ></small>
    }
    @if (showError) {
      <small
        class="text-[var(--p-form-field-invalid-placeholder-color)] italic ml-[var(--p-inputtext-padding-x)]"
      >
        {{ 'formly.validation.required' | translate }}
      </small>
    }

    <custom-drawer
      [(visible)]="visible"
      (onShow)="onShow()"
      (onHide)="onHide()"
      [styleClass]="'h-auto'"
    >
      <ng-template #header>
        <div class="title" [innerHTML]="props.label"></div>
      </ng-template>
      <ng-template #content>
        <div
          class="flex flex-col gap-4 px-[var(--p-dialog-header-padding)] py-28 h-fit min-h-full"
        >
          <ng-container #fieldComponent></ng-container>
        </div>
      </ng-template>
      <ng-template #footer>
        <div class="w-full text-right">
          <p-button
            [label]="props.buttonLabel ?? 'Set'"
            size="small"
            (onClick)="set()"
          ></p-button>
        </div>
      </ng-template>
    </custom-drawer>
  `,
})
export class DrawerWrapperFormField extends FieldWrapper<
  FormlyFieldConfig<DrawerWrapperProps>
> {
  @ViewChild('inputDrawer') input!: ElementRef<HTMLInputElement>;
  @ViewChild('drawer') drawer!: Drawer;
  visible: boolean = false;

  renderer = inject(Renderer2);
  injector = inject(Injector);
  control = new FormControl('', { nonNullable: true });

  onHide() {
    this.props.onShow?.set(undefined);
  }

  onShow() {
    this.props.onShow?.set('onshow');
  }

  value() {
    if (this.formControl.valid) {
      return this.props.formatLabel(this.field);
    } else {
      return null;
    }
  }

  set() {
    if (this.formControl.valid) {
      if (this.props.isTextView) {
        this.visible = false;
        this.onHide();
      } else {
        this.props.setter(this.field).then((res: string) => {
          if (res) {
            this.visible = false;
            this.onHide();
          }
        });
        this.props.status = true;
      }
    } else {
      const controls = (this.formControl as FormGroup).controls;
      Object.keys(controls).forEach((el) => {
        if (controls[el].invalid) {
          controls[el].markAsDirty();
        }
      });
      this.props.status = false;
    }
    if (this.props.status == false) {
      this.control.markAsDirty();
      this.control.markAllAsTouched();
      this.control.setErrors({ invalid: true, required: true });
    }
  }
}
